import * as React from 'react';
import { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import './AwardCard.scss';
import { AppUser } from 'Src/views/App/App';
import { AlbumGallery } from 'Src/components/AlbumGallery/AlbumGallery';
import { Photo } from 'Src/models/photo';
import { Video } from 'Src/models/video';
import { getAllAwardEventPhotos } from 'Src/helpers/service/admin/awardEventPhotos';
import { getAllAwardEventVideos } from 'Src/helpers/service/admin/awardEventVideos';
import { AwardItem } from 'Src/helpers/service/parent/awards';

interface Props {
  item: AwardItem;
  hideStudent?: boolean;
  user?: AppUser;
}

const extractPhotoUrl = (val: any): string | undefined => {
  if (!val) return undefined;

  if (typeof val.picUrl === 'string' && val.picUrl) return val.picUrl;

  if (typeof val.key === 'string' && val.key) {
    const base = (window as any)?.apiImg;
    return base ? `${base}/images/${val.key}` : val.key;
  }

  const candidates = ['url', 'secure_url', 'pic', 'photoUrl', 'path', 'publicUrl', 'public_url'];
  for (const k of candidates) {
    const v = (val as any)[k];
    if (typeof v === 'string' && v) return v;
  }

  if (val && typeof val === 'object' && 'data' in val) {
    return extractPhotoUrl((val as any).data);
  }

  return undefined;
};

const normalizePhotos = (raw: any): Photo[] => {
  const arr = Array.isArray(raw) ? raw : raw && Array.isArray((raw as any).items) ? (raw as any).items : [];
  return arr
    .map((p: any, idx: number) => {
      const picUrl = extractPhotoUrl(p);
      const id = p.id || p._id || p.photoId || p.key || String(idx);
      return picUrl ? { id, picUrl, name: p.name || p.originalFileName || '', description: p.description || '' } : null;
    })
    .filter(Boolean) as Photo[];
};

export const AwardCard: React.FC<Props> = ({ item, hideStudent = false, user }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [tab, setTab] = useState<'details' | 'gallery'>('details');

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [isGalleryLoading, setGalleryLoading] = useState(false);

  const awardId = item.id;

  const date = item.awardedAt ? moment(item.awardedAt).format('DD MMM YYYY') : '';
  const imageSrc =
    item.awardType?.picture || item.house?.pic || item.school?.pic || '/static/img/award-placeholder.svg';

  useEffect(() => {
    let cancelled = false;

    const loadMedia = async () => {
      if (!isOpen || tab !== 'gallery' || !awardId) return;
      setGalleryLoading(true);
      try {
        const [rawPhotos, rawVideos] = await Promise.all([
          getAllAwardEventPhotos(user, awardId).catch(() => []),
          getAllAwardEventVideos(user, awardId).catch(() => [])
        ]);

        const ph = normalizePhotos(rawPhotos);

        let vd: Video[] = Array.isArray(rawVideos)
          ? rawVideos
          : rawVideos && Array.isArray((rawVideos as any).items)
          ? (rawVideos as any).items
          : [];
        vd = vd.filter(v => v?.status === 'COMPLETE' && Array.isArray(v?.streamData) && v.streamData.length > 0);

        if (!cancelled) {
          setPhotos(ph);
          setVideos(vd);
        }
      } catch {
        if (!cancelled) {
          setPhotos([]);
          setVideos([]);
        }
      } finally {
        if (!cancelled) setGalleryLoading(false);
      }
    };

    loadMedia();
    return () => {
      cancelled = true;
    };
  }, [isOpen, tab, awardId, user]);

  return (
    <>
      <div
        className="card h-100 AwardCard"
        role="button"
        tabIndex={0}
        onClick={() => {
          setIsOpen(true);
          setTab('details');
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            setIsOpen(true);
            setTab('details');
          }
        }}
      >
        <div className="card-body pb-2">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <h6 className="mb-0" title={item.awardType?.name || 'Award'}>
              {item.awardType?.name || 'Award'}
            </h6>
            <span className="badge badge-light">{date}</span>
          </div>

          <div className="awardCardMain">
            {imageSrc && (
              <div className="award-card-thumb">
                <img src={imageSrc} alt={item.awardType?.name || item.houseName || 'Award'} />
              </div>
            )}

            <div className="awardCardSummary">
              {!hideStudent && item.student && (
                <div className="awardCardSummary__child">
                  <span>Child</span>
                  <strong>{item.student.displayName}</strong>
                </div>
              )}

              <div className="awardCardSummary__chips">
                {item.points > 0 && (
                  <span className="ac-points-chip" title={`${item.points} points`}>
                    {item.points} pts
                  </span>
                )}

                {item.houseName ? (
                  <span className="awardCardSummary__house">
                    <span>House</span> {item.houseName}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {(item.awardedByName || item.reason) && (
            <div className="awardCardMeta">
              {item.awardedByName && (
                <div className="awardCardMeta__row">
                  <span>Awarded by</span>
                  <strong>{item.awardedByName}</strong>
                </div>
              )}
              {item.reason && (
                <div className="awardCardMeta__row awardCardMeta__reason">
                  <span>Reason</span>
                  <p>{item.reason}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isOpen && (
        <SimpleModal
          isOpen={isOpen}
          title={item.awardType?.name || 'Award details'}
          onCloseClick={() => setIsOpen(false)}
          customClass="awardDetailsDrawer"
          side="right"
          showFooter={false}
        >
          <div className="awardDetailsTabs" role="tablist">
            <button type="button" className={tab === 'details' ? 'is-active' : ''} onClick={() => setTab('details')}>
              Details
            </button>
            <button type="button" className={tab === 'gallery' ? 'is-active' : ''} onClick={() => setTab('gallery')}>
              Gallery
            </button>
          </div>

          {tab === 'details' && (
            <div className="awardDetails">
              <div className="awardDetails__hero">
                <div className="awardDetails__image">
                  {imageSrc && <img src={imageSrc} alt={item.awardType?.name || item.houseName || 'Award'} />}
                </div>
                <div className="awardDetails__heading">
                  <span>Award</span>
                  <strong>{item.awardType?.name || 'Award'}</strong>
                  {item.student && <small>{item.student.displayName}</small>}
                </div>
                {item.points > 0 && (
                  <span className="awardDetails__points">
                    {item.points}
                    <small>pts</small>
                  </span>
                )}
              </div>

              <div className="awardDetails__meta">
                <div>
                  <span>Awarded on</span>
                  <strong>{date || '—'}</strong>
                </div>
                {item.houseName && (
                  <div>
                    <span>House</span>
                    <strong>{item.houseName}</strong>
                  </div>
                )}
                {item.awardedByName && (
                  <div>
                    <span>Awarded by</span>
                    <strong>{item.awardedByName}</strong>
                  </div>
                )}
              </div>

              <section className="awardDetails__reason">
                <span>Reason</span>
                <p>{item.reason || 'No reason was provided.'}</p>
              </section>
            </div>
          )}

          {tab === 'gallery' && (
            <div className="awardDetailsGallery">
              {isGalleryLoading ? (
                <div className="awardDetailsGallery__state">Loading gallery…</div>
              ) : photos.length === 0 && videos.length === 0 ? (
                <div className="awardDetailsGallery__empty">
                  <div className="awardDetailsGallery__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <rect x="3" y="4" width="18" height="16" rx="3" />
                      <circle cx="9" cy="10" r="2" />
                      <path d="m5 18 5-5 3 3 2-2 4 4" />
                    </svg>
                  </div>
                  <strong>No media yet</strong>
                  <p>Photos and videos added to this award will appear here.</p>
                </div>
              ) : (
                <AlbumGallery photos={photos} videos={videos} showDelete={false} showEmptySections={false} />
              )}
            </div>
          )}
        </SimpleModal>
      )}
    </>
  );
};
